"use client";

import React, { useMemo } from "react";
import { MapPin, Clock, Navigation } from "lucide-react";
import { Button, Card } from "antd";
import { useTranslation } from "react-i18next";
import "./PharmacyCard.css";
import ph from "../../../assets/images/ph.png";
import pharmacyDefault from "../../../assets/svg/pharmacy-default.png";
import usePlaces, { getImageSrcFor, getDirectionsUrlFor } from "../../hooks/usePlaces";

export default function PharmacyCard({ pharmacy, onClick, selectedPharmacy }) {
  const { t } = useTranslation();

  // Helpers depuis le hook (pas d’URL hardcodée ici)
  const {
    getImageSrc = getImageSrcFor,
    getDirectionsUrl = getDirectionsUrlFor,
  } = usePlaces();

  const isSelected = selectedPharmacy?.place_id === pharmacy?.place_id;

  const isOpen = useMemo(() => {
    return pharmacy?.is_open_now ?? pharmacy?.opening_hours?.open_now ?? true;
  }, [pharmacy]);

  const hoursDays = pharmacy?.hours_days || t("locators.card.hoursDaysDefault");
  const openTime = pharmacy?.open_time || "08:00 AM";
  const closeTime = pharmacy?.close_time || "08:00 PM";
  const address =
    pharmacy?.address || pharmacy?.vicinity || t("locators.card.unknownAddress");

  // ⛔️ N’essaie JAMAIS d’appeler Google Photo : getImageSrc() renvoie null si c’est une URL Google
  const imgSrc = useMemo(() => {
    const out = getImageSrc(pharmacy);
    if (!out) return pharmacyDefault; // fallback SVG caduceus
    return out;
  }, [pharmacy, getImageSrc]);

  const handleDirections = (e) => {
    e.stopPropagation();
    window.open(getDirectionsUrl(pharmacy), "_blank");
  };

  return (
    <Card
      key={pharmacy?.place_id || pharmacy?.id}
      className={`phcc-card ${isSelected ? "is-selected" : ""}`}
      hoverable
      onClick={() => onClick?.(pharmacy)}
      bodyStyle={{ padding: 12 }}
    >
      <div className="phc-row">
        {/* image */}
        <div className="phc-thumb">
          <img
            src={imgSrc}
            alt={pharmacy?.name || t("locators.card.imageAlt")}
            onLoad={() =>
              undefined
            }
            onError={(e) => {
              console.warn("[PharmacyCard] image error → fallback", {
                place_id: pharmacy?.place_id,
                tried: imgSrc,
              });
              e.currentTarget.src = pharmacyDefault;
            }}
          />
        </div>

        {/* nom + adresse */}
          <div className="phc-info">
            <div className="phc-name">
              {pharmacy?.name || t("locators.card.unnamed")}
            </div>
          <div className="phc-address">
            <MapPin className="phc-ico" />
            <span>{address}</span>
          </div>
        </div>

        {/* horaires */}
        <div className="phc-hours">
          <Clock className="phc-ico" />
          <div className="phc-hours-col">
            <div className="phc-days">{hoursDays}</div>
            <div className="phc-times">
              <span>
                {t("locators.card.openLabel")} &nbsp;{openTime}
              </span>
              <span className="phc-spacer" />
              <span>
                {t("locators.card.closeLabel")} &nbsp;{closeTime}
              </span>
            </div>
          </div>
        </div>

        {/* statut */}
        <div className="phc-status">
          <span className={`phc-chip ${isOpen ? "is-open" : "is-closed"}`}>
            {isOpen ? t("locators.card.statusOpen") : t("locators.card.statusClosed")}
          </span>
        </div>

        {/* bouton directions */}
        <div className="phc-actions">
          <Button
            className="phc-btn"
            icon={<Navigation className="phc-btn-ico" />}
            onClick={handleDirections}
          >
            {t("locators.card.directions")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
